import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CheckCircle, ChevronRight, AlertTriangle, Save, X } from 'lucide-react';
import { cn } from '../ui/utils';
import { toast } from 'sonner';

interface WellStatusCardProps {
    status: string;
    maintenanceTicket?: any;
    onUpdateStatus: (newStatus: string) => void;
    onRequestMaintenance: () => void;
    isDarkMode: boolean;
}

export function WellStatusCard({
    status,
    maintenanceTicket,
    onUpdateStatus,
    onRequestMaintenance,
    isDarkMode
}: WellStatusCardProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState(status);

    const getStatusColor = (s: string) => {
        switch (s) {
            case 'active': return 'bg-emerald-500';
            case 'maintenance': return 'bg-amber-500';
            case 'inactive': return 'bg-slate-400';
            default: return 'bg-slate-400';
        }
    };

    const handleSave = () => {
        if (selectedStatus === 'maintenance' && status !== 'maintenance') {
            // Trigger maintenance flow
            onRequestMaintenance();
            setIsEditing(false);
            return;
        }
        onUpdateStatus(selectedStatus);
        setIsEditing(false);
    };

    return (
        <Card className={cn("overflow-hidden", isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200 shadow-sm")}>
            {/* Maintenance Banner if active */}
            {status === 'maintenance' && maintenanceTicket && (
                <div className="bg-amber-500/10 border-b border-amber-500/20 p-3 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <h4 className="text-sm font-bold text-amber-700 dark:text-amber-400">Maintenance Active</h4>
                            <Badge variant="outline" className="text-[10px] h-5 px-1.5 border-amber-500/30 text-amber-600 dark:text-amber-400">
                                {maintenanceTicket.ticketNumber}
                            </Badge>
                        </div>
                        <p className="text-xs text-slate-600 dark:text-slate-400 line-clamp-2">
                            {maintenanceTicket.title} - {maintenanceTicket.description}
                        </p>
                    </div>
                </div>
            )}

            <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                    <h3 className={cn("font-semibold", isDarkMode ? "text-white" : "text-slate-900")}>Well Status</h3>
                    {!isEditing && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                setSelectedStatus(status);
                                setIsEditing(true);
                            }}
                            className="h-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 px-2"
                        >
                            Change
                        </Button>
                    )}
                </div>

                {isEditing ? (
                    <div className="space-y-3 animate-in fade-in slide-in-from-top-1">
                        <div className="space-y-2">
                            {['active', 'maintenance', 'inactive'].map((s) => (
                                <div
                                    key={s}
                                    onClick={() => setSelectedStatus(s)}
                                    className={cn(
                                        "flex items-center justify-between p-3 rounded-xl border-2 transition-all cursor-pointer",
                                        selectedStatus === s
                                            ? "border-blue-600 bg-blue-50/50 dark:bg-blue-900/20"
                                            : isDarkMode
                                                ? "border-slate-800 bg-slate-800/50 text-slate-400 hover:bg-slate-800"
                                                : "border-gray-100 bg-gray-50 text-slate-600 hover:bg-gray-100"
                                    )}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={cn("w-3 h-3 rounded-full shadow-sm ring-2 ring-white/10", getStatusColor(s))} />
                                        <span className="font-medium capitalize text-sm">{s}</span>
                                    </div>
                                    {selectedStatus === s && <CheckCircle className="w-5 h-5 text-blue-600" />}
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-2 pt-1">
                            <Button
                                variant="outline"
                                className="flex-1"
                                onClick={() => setIsEditing(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                                onClick={handleSave}
                            >
                                <Save className="w-4 h-4 mr-2" />
                                Save
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className={cn(
                        "flex items-center gap-3 p-3 rounded-xl border transition-colors",
                        isDarkMode ? "bg-slate-800/50 border-slate-800" : "bg-slate-50 border-slate-100"
                    )}>
                        <div className={cn("w-4 h-4 rounded-full shadow-sm ring-2 ring-white/10", getStatusColor(status))} />
                        <span className={cn("font-medium capitalize flex-1", isDarkMode ? "text-slate-200" : "text-slate-700")}>
                            {status}
                        </span>
                        {/* <div className="text-xs text-muted-foreground">
                            Last updated 2 days ago
                        </div> */}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
